import Dexie from 'dexie'
import { digest } from 'jsum'

const SECRET = Buffer.from(
  Uint8Array.from([
    169, 90, 200, 150, 217, 221, 8, 18, 75, 149, 200, 169, 193, 141, 44, 214,
    198, 187, 173, 61,
  ])
).toString('base64')

function writeChecksum(data: any, table: string) {
  if (typeof data !== 'object') {
    return data
  }
  // Checksums are calculated incorrectly when the object data includes functions or other data types that can not be
  // saved to the database. We must convert to a plain object before we calculate the checksum.
  const sanitizedData = JSON.parse(JSON.stringify(data))
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __checksum, ...rest } = sanitizedData
  return {
    ...rest,
    __checksum: digest(
      { ...rest, __secret: SECRET, __table: table },
      'sha256',
      'base64'
    ),
  }
}

function validateChecksum(data: any, table: string) {
  if (typeof data !== 'object') {
    return data
  }
  const { __checksum, ...rest } = data
  const checksum = digest(
    { ...rest, __secret: SECRET, __table: table },
    'sha256',
    'base64'
  )
  if (__checksum && checksum !== __checksum) {
    throw new TypeError('Data modified')
  }
  return data
}

export class DexieSchemaWithChecksum extends Dexie {
  protected writeChecksum(data: any, table: string) {
    return writeChecksum(data, table)
  }

  protected useValidateChecksumsMiddleware() {
    this.use({
      stack: 'dbcore',
      name: 'validate',
      level: 0,
      create(downlevelDatabase) {
        return {
          ...downlevelDatabase,
          table(tableName: string) {
            const table = downlevelDatabase.table(tableName)

            return {
              ...table,
              openCursor(request) {
                return table.openCursor(request).then(cursor => {
                  if (!cursor) {
                    return cursor
                  }
                  return Object.create(cursor, {
                    continue: {
                      get() {
                        return cursor.continue
                      },
                    },
                    continuePrimaryKey: {
                      get() {
                        return cursor.continuePrimaryKey
                      },
                    },
                    key: {
                      get() {
                        return cursor.key
                      },
                    },
                    value: {
                      get() {
                        return validateChecksum(cursor.value, tableName)
                      },
                    },
                  })
                })
              },
              get(request) {
                return table
                  .get(request)
                  .then(item => validateChecksum(item, tableName))
              },
              getMany(request) {
                return table.getMany(request).then(items => {
                  return items.map(item => validateChecksum(item, tableName))
                })
              },
              query(request) {
                return table.query(request).then(response => {
                  return Dexie.Promise.all(
                    response.result.map(item =>
                      validateChecksum(item, tableName)
                    )
                  ).then(result => ({
                    ...response,
                    result,
                  }))
                })
              },
              mutate(request) {
                if (request.type === 'add' || request.type === 'put') {
                  return Dexie.Promise.all(
                    request.values.map(item => writeChecksum(item, tableName))
                  ).then(values =>
                    table.mutate({
                      ...request,
                      values,
                    })
                  )
                }
                return table.mutate(request)
              },
            }
          },
        }
      },
    })
  }
}
